import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import Button from '../../../components/Button'
import SetUpPartnerSection from './SetUpPartnerSection'
import CampaignsSection from './CampaignsSection'
import ReportsSection from './ReportsSection'
import ButtonClose from '../../../features/components/Buttons/ButtonClose'

import { showToasts } from '../../../helpers/toasts'
import { redirectTo } from '../../../helpers/url'

import { selectedPartnershipSelector } from '../../../modules/selectors/partnerships'
import { selectedPlatformSelector } from '../../../modules/selectors/app'

import { ROUTE_PARAMS, ROUTES } from '../../../constants/routes'
import { TOAST_TYPE } from '../../../constants/other'
import { PLATFORMS_TO_PROVIDER, PROVIDER_TO_PLATFORMS } from '../../../constants/selectLists/platformList'

import useSummaryPagesStyles from '../../../styles/common/summaryPages'
import useStyles from './styles'

const SET_UP_PARTNER_SECTION = 'setUpPartnerSection'
const CAMPAIGN_SECTION = 'campaignSection'
const REPORTS_SECTION = 'reportsSection'

const AdvancedPartnershipsContent = ({ WelcomeMessage, isDigitalRightExist, openLatestDigitalRight }) => {
  const summaryPagesClasses = useSummaryPagesStyles()

  const { t } = useTranslation()

  const [activeSection, setActiveSection] = useState('')

  const classes = useStyles(!!activeSection)

  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const selectedPlatform = useSelector(selectedPlatformSelector)

  const { providers = [] } = selectedPartnership

  const goToCampaignHubButtonHandler = useCallback(() => {
    if (providers?.length) {
      redirectTo(ROUTES.campaigns, {
        [ROUTE_PARAMS.platform]: providers.includes(PLATFORMS_TO_PROVIDER[selectedPlatform])
          ? selectedPlatform
          : PROVIDER_TO_PLATFORMS[providers[0]]
      })
    } else
      showToasts({
        type: TOAST_TYPE.info,
        message: "You haven't added any ad accounts yet, please add one on the partnerships settings page.."
      })
  }, [providers, selectedPlatform])

  const setActiveSectionHandler = useCallback(sectionName => {
    setActiveSection(previousSection => {
      // if the section is already active, close it
      if (previousSection === sectionName) {
        return ''
      }

      return sectionName
    })
  }, [])

  const ActiveSection = useMemo(() => {
    switch (activeSection) {
      case SET_UP_PARTNER_SECTION:
        return <SetUpPartnerSection />
      case CAMPAIGN_SECTION:
        return <CampaignsSection goToCampaignHubButtonHandler={goToCampaignHubButtonHandler} />
      case REPORTS_SECTION:
        return <ReportsSection goToCampaignHubButtonHandler={goToCampaignHubButtonHandler} />
      default:
        return null
    }
  }, [activeSection, goToCampaignHubButtonHandler])

  return (
    <>
      {WelcomeMessage}
      <div className={summaryPagesClasses.welcomeButtonsWrapper}>
        <Button onClick={() => setActiveSectionHandler(SET_UP_PARTNER_SECTION)}>
          {t('Set up a new partner or sponsor')}
        </Button>
        <Button onClick={() => setActiveSectionHandler(CAMPAIGN_SECTION)}>{t('Launch your own ad campaign')}</Button>
        <Button onClick={() => setActiveSectionHandler(REPORTS_SECTION)}>
          {t('View or download a campaign report')}
        </Button>
        {isDigitalRightExist && <Button onClick={openLatestDigitalRight}>{t('Upload a digital ad creative')}</Button>}
      </div>
      <div className={classes.activeSectionContainer}>
        <div className={classes.actionSection}>
          {activeSection && <ButtonClose className={classes.closeBtn} onClick={() => setActiveSection('')} />}
          {ActiveSection}
        </div>
      </div>
    </>
  )
}

AdvancedPartnershipsContent.propTypes = {
  WelcomeMessage: PropTypes.node.isRequired,
  isDigitalRightExist: PropTypes.bool.isRequired,
  openLatestDigitalRight: PropTypes.func.isRequired
}

export default AdvancedPartnershipsContent
